import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { useField, UseFieldConfig } from 'react-final-form'
import { required } from 'forms/validators'
import { checkFieldError, getFieldErrorMessage } from 'helpers/form'
import { useActions } from 'hooks'
import isEmpty from 'lodash/isEmpty'
import { DeliveryTypeEnum } from 'modules/deliveryInfo/types'
import { TOrderCreatePoint } from 'modules/order/types/orderCreateTypes'
import { TScheduleDay } from 'modules/terminal/types'
import { CourierDeliveryInfo, OrderItemClient } from 'schema'

import { OrderCreateFormRoute } from '../components/OrderCreate/OrderCreateFormRoute'

interface IProps extends Pick<UseFieldConfig<TOrderCreatePoint>, 'validate'> {
  customerId: string
  customerTypeValue: OrderItemClient.type
  errorAddress: string
  name: string
  searchAddressValue: string
  setErrorAddress: React.Dispatch<React.SetStateAction<string>>
  setSearchAddressValue: React.Dispatch<React.SetStateAction<string>>
  setTerminalId: React.Dispatch<React.SetStateAction<string>>
  type: CourierDeliveryInfo.pointType
  className?: string
  departureTerminalId?: string
  setDepartureTerminalSchedule?: React.Dispatch<React.SetStateAction<TScheduleDay[] | undefined>>
  setDestinationTerminalSchedule?: React.Dispatch<React.SetStateAction<TScheduleDay[] | undefined>>
  setPointType?: React.Dispatch<React.SetStateAction<DeliveryTypeEnum | undefined>>
}

export const FieldOrderCreateFormRoute = memo<IProps>(function FieldOrderCreateFormRoute({
  className,
  name,
  type,
  validate,
  customerId,
  searchAddressValue,
  setSearchAddressValue,
  customerTypeValue,
  setTerminalId,
  departureTerminalId,
  setPointType,
  setDepartureTerminalSchedule,
  setDestinationTerminalSchedule
}) {
  const { input, meta } = useField<TOrderCreatePoint>(name, { validate })
  const { meta: terminalIdMeta } = useField<string>(`${name}.terminalId`, {
    validate: required
  })
  const { initial } = meta

  const { setOrderSelectedTerminal, setOrderAddIsCourier } = useActions()

  useEffect(() => {
    if (initial) {
      if (initial.isCourier !== undefined) {
        setPointType && setPointType(initial.isCourier ? DeliveryTypeEnum.courier : DeliveryTypeEnum.terminal)
        setOrderAddIsCourier({ key: type, value: Boolean(initial.isCourier) })
      }

      setOrderSelectedTerminal({ key: type, value: initial.terminal })
      setDepartureTerminalSchedule && setDepartureTerminalSchedule(initial.terminal?.schedule)

      if (type === CourierDeliveryInfo.pointType.DEPARTURE) {
        setTerminalId(initial.terminal?.id)
      }

      if (type === CourierDeliveryInfo.pointType.DESTINATION) {
        setTerminalId(initial.terminal?.id)
      }
    }
    // eslint-disable-next-line
  }, [])

  const handleChange = useCallback(
    (point: TOrderCreatePoint) => {
      const { addressDetail, isCourier, terminalId, terminal } = point
      const data = {} as unknown as TOrderCreatePoint

      if (typeof addressDetail !== 'undefined') {
        data.addressDetail = addressDetail
      }

      if (typeof isCourier !== 'undefined') {
        data.isCourier = isCourier
      }

      if (typeof terminalId !== 'undefined') {
        data.terminalId = terminalId
        data.terminal = terminal
      }

      if (!isEmpty(data)) {
        input.onChange(data)
      }
    },
    [input]
  )

  const error = useMemo(() => {
    const routeMetaError = checkFieldError(meta)
    const terminalIdMetaError = checkFieldError(terminalIdMeta)

    return getFieldErrorMessage(
      (typeof routeMetaError !== 'object' && routeMetaError) ||
        (typeof terminalIdMetaError !== 'object' && terminalIdMetaError)
    )
  }, [meta, terminalIdMeta])

  return (
    <div className={className} data-name={name}>
      <OrderCreateFormRoute
        customerId={customerId}
        customerTypeValue={customerTypeValue}
        departureTerminalId={departureTerminalId}
        error={error}
        initial={initial}
        name={name}
        onChange={handleChange}
        pointType={type}
        searchAddressValue={searchAddressValue}
        setDepartureTerminalId={type === CourierDeliveryInfo.pointType.DEPARTURE ? setTerminalId : undefined}
        setDepartureTerminalSchedule={setDepartureTerminalSchedule}
        setDestinationTerminalId={type === CourierDeliveryInfo.pointType.DESTINATION ? setTerminalId : undefined}
        setDestinationTerminalSchedule={setDestinationTerminalSchedule}
        setPointType={setPointType}
        setSearchAddressValue={setSearchAddressValue}
        value={input.value}
      />
    </div>
  )
})
